/**
 * Test component to validate video call monitoring telemetry
 * This component simulates video call monitoring events for testing
 */

import React, { useState, useCallback } from 'react';
import { Button } from '@/components/ui/button';
import { useMonitoringWithTelemetryWrapper } from '@/hooks/video-call/useMonitoringWithTelemetryWrapper';
import { useTelemetryMetrics } from '@/hooks/telemetry/useTelemetryMetrics';

export function VideoCallTelemetryTest() {
  const [testResults, setTestResults] = useState<string[]>([]);
  const [callId] = useState(`test-call-${Date.now()}`);
  const [parentTrace, setParentTrace] = useState<any>(null);

  const telemetryWrapper = useMonitoringWithTelemetryWrapper({
    callId,
    organizationId: 'test-org-123',
    callType: 'video_interview',
    enableViolationTracking: true,
    enableUserInteractionTracking: true,
    enablePerformanceTracking: true,
  });

  const { recordViolation } = useTelemetryMetrics();

  // Simulate violation callback
  const mockAddViolation = useCallback((type: any, message: string, severity: any, duration?: number) => {
    const result = `✅ Violation Added: ${type} (${severity}) - ${message}${duration ? ` [${duration}ms]` : ''}`;
    setTestResults(prev => [...prev, result]);
    console.log(result);
  }, []);

  // Simulate other callbacks
  const mockShowTabChangePenalty = useCallback(() => {
    const result = '✅ Tab Change Penalty Triggered';
    setTestResults(prev => [...prev, result]);
    console.log(result);
  }, []);

  const mockShowWindowBlurPenalty = useCallback(() => {
    const result = '✅ Window Blur Penalty Triggered';
    setTestResults(prev => [...prev, result]);
    console.log(result);
  }, []);

  const mockPauseCall = useCallback((reason: "monitor_detected") => {
    const result = `✅ Call Paused: ${reason}`;
    setTestResults(prev => [...prev, result]);
    console.log(result);
  }, []);

  const mockResumeCall = useCallback(() => {
    const result = '✅ Call Resumed';
    setTestResults(prev => [...prev, result]);
    console.log(result);
  }, []);

  const mockEndCall = useCallback((reason: string) => {
    const result = `✅ Call Ended: ${reason}`;
    setTestResults(prev => [...prev, result]);
    console.log(result);
  }, []);

  // Create telemetry-enhanced callbacks
  const telemetryEnhancedAddViolation = telemetryWrapper.wrapAddViolation(mockAddViolation);
  const telemetryEnhancedTabChangePenalty = telemetryWrapper.wrapTabChangePenalty(mockShowTabChangePenalty);
  const telemetryEnhancedWindowBlurPenalty = telemetryWrapper.wrapWindowBlurPenalty(mockShowWindowBlurPenalty);
  const telemetryEnhancedPauseCall = telemetryWrapper.wrapPauseCall(mockPauseCall);
  const telemetryEnhancedResumeCall = telemetryWrapper.wrapResumeCall(mockResumeCall);
  const telemetryEnhancedEndCall = telemetryWrapper.wrapEndCall(mockEndCall);

  const clearResults = () => {
    setTestResults([]);
  };

  const testCallStart = () => {
    if (!parentTrace) {
      telemetryWrapper.startMonitoringTelemetry();
      setParentTrace(true); // Set flag that monitoring is started
      setTestResults(prev => [...prev, '🚀 Monitoring Telemetry Started - All events will be recorded']);
    } else {
      setTestResults(prev => [...prev, '⚠️ Monitoring telemetry already started']);
    }
  };

  const testTabChangeViolation = () => {
    telemetryEnhancedAddViolation('tab_change', 'User switched to another tab', 'warning', 3000);
  };

  const testMultipleMonitorViolation = () => {
    telemetryEnhancedAddViolation('multiple_monitors', 'External monitor detected', 'critical');
  };

  const testScreenShareViolation = () => {
    telemetryEnhancedAddViolation('screen_share_lost', 'Unauthorized screen sharing', 'penalty', 5000);
  };

  const testTabChangePenalty = () => {
    telemetryEnhancedTabChangePenalty();
  };

  const testWindowBlurPenalty = () => {
    telemetryEnhancedWindowBlurPenalty();
  };

  const testCallPause = () => {
    telemetryEnhancedPauseCall('monitor_detected');
  };

  const testCallResume = () => {
    telemetryEnhancedResumeCall();
  };

  const testCallEnd = () => {
    telemetryEnhancedEndCall('user_ended');
  };

  const testEndParentTrace = () => {
    if (parentTrace) {
      telemetryWrapper.endMonitoringTelemetry('test_completed');
      setParentTrace(null);
      setTestResults(prev => [...prev, '🏁 Monitoring Telemetry Ended - Session complete']);
    } else {
      setTestResults(prev => [...prev, '⚠️ No monitoring telemetry to end']);
    }
  };

  const testDirectMetrics = () => {
    recordViolation('test_violation', 'medium', {
      test: 'direct_call',
      call_id: callId,
    });
    setTestResults(prev => [...prev, '📊 Direct Metrics Recorded - Check console']);
  };

  return (
    <div className="max-w-4xl mx-auto p-6 bg-card rounded-lg shadow-lg">
      <div className="mb-6">
        <h2 className="text-2xl font-bold text-foreground mb-2">
          🧪 Video Call Telemetry Test
        </h2>
        <p className="text-muted-foreground">
          Test the OpenTelemetry integration for video call monitoring events.
          <br />
          <strong>Call ID:</strong> {callId}
        </p>
        <div className="mt-2 p-2 bg-secondary rounded text-sm text-foreground">
          💡 Open browser console to see telemetry logs and traces
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-3 gap-3 mb-6">
        <Button onClick={testCallStart} variant="outline" size="sm">
          🚀 Start Call
        </Button>
        
        <Button onClick={testTabChangeViolation} variant="outline" size="sm">
          🔄 Tab Change
        </Button>
        
        <Button onClick={testMultipleMonitorViolation} variant="outline" size="sm">
          🖥️ Multi Monitor
        </Button>
        
        <Button onClick={testScreenShareViolation} variant="outline" size="sm">
          📺 Screen Share
        </Button>
        
        <Button onClick={testTabChangePenalty} variant="outline" size="sm">
          ⚠️ Tab Penalty
        </Button>
        
        <Button onClick={testWindowBlurPenalty} variant="outline" size="sm">
          👁️ Blur Penalty
        </Button>
        
        <Button onClick={testCallPause} variant="outline" size="sm">
          ⏸️ Pause Call
        </Button>
        
        <Button onClick={testCallResume} variant="outline" size="sm">
          ▶️ Resume Call
        </Button>
        
        <Button onClick={testCallEnd} variant="outline" size="sm">
          🔚 End Call
        </Button>
        
        <Button onClick={testEndParentTrace} variant="outline" size="sm">
          🏁 End Parent Trace
        </Button>
        
        <Button onClick={testDirectMetrics} variant="outline" size="sm">
          📊 Direct Metrics
        </Button>
        
        <Button onClick={clearResults} variant="outline" size="sm">
          🧹 Clear Results
        </Button>
      </div>

      <div className="space-y-4">
        <h3 className="text-lg font-semibold text-foreground">
          Test Results ({testResults.length})
        </h3>
        
        <div className="bg-muted rounded-lg p-4 max-h-96 overflow-y-auto">
          {testResults.length === 0 ? (
            <p className="text-muted-foreground italic">
              No tests run yet. Click the buttons above to test telemetry.
            </p>
          ) : (
            <div className="space-y-2">
              {testResults.map((result, index) => (
                <div key={index} className="text-sm font-mono bg-card p-2 rounded border">
                  <span className="text-muted-foreground mr-2">
                    [{new Date().toLocaleTimeString()}]
                  </span>
                  {result}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      <div className="mt-6 p-4 bg-mid-bg rounded-lg">
        <h4 className="font-semibold text-mid mb-2">Expected Telemetry Output (Single Parent Trace):</h4>
        <ul className="text-sm text-mid space-y-1">
          <li>• <strong>Parent Trace:</strong> video-call.session (spans entire call duration)</li>
          <li>• <strong>Child Spans:</strong> video_call.violation.*, video_call.paused, etc.</li>
          <li>• <strong>Console logs:</strong> "📊 [Telemetry] Started/Ended video call trace"</li>
          <li>• <strong>Metrics:</strong> voxi.video.violations, voxi.user.interactions</li>
          <li>• <strong>OTLP export:</strong> Single trace with all events as child spans</li>
        </ul>
      </div>
    </div>
  );
}